import { z } from 'zod';
import { FleetRecoveryPathPolicy, FLEET_RECOVERY_MAX_ITEMS, fleetRecoveryHttpsSchema,
  fleetRecoveryIdSchema, fleetRecoveryPathSchema, fleetRecoverySha256Schema,
  fleetRecoveryUint64Schema, fleetRecoveryVersionSchema } from './scalars';
import { ownedTargetSchema, ownershipRuleSchema } from './targets';
import { recoveryAuthorityTransitionSchema } from './authority';
import { FleetRecoveryRebootManifest } from './FleetRecoveryRebootManifest';
import type { InstallationManifest } from '../fleetRecoveryTypes';

export const recoveryArtifactSchema = z.object({
  role: z.enum(['installer', 'driver', 'updater', 'rescue', 'worker']), sha256: fleetRecoverySha256Schema,
  sizeBytes: fleetRecoveryUint64Schema.refine(value => value !== '0', 'Artifact cannot be empty'),
  url: fleetRecoveryHttpsSchema, publisherId: fleetRecoveryIdSchema,
  architecture: z.enum(['x64', 'arm64']), protocolMin: fleetRecoveryVersionSchema,
  protocolMax: fleetRecoveryVersionSchema,
}).strict().refine(artifact => artifact.protocolMin <= artifact.protocolMax, 'Inverted protocol range');

export const readinessProbeSchema = z.object({
  targetId: fleetRecoveryIdSchema,
  kind: z.enum(['hash', 'registration', 'service-identity', 'service-protocol', 'device-health', 'deferred-setup', 'device-binding']),
  required: z.boolean(), deadlineMs: z.number().int().min(1).max(300000),
}).strict();

export const installationManifestSchema = z.object({
  schemaVersion: z.literal(2), productVersion: z.string().min(1).max(128), platform: z.literal('windows'),
  requiredFreeBytes: fleetRecoveryUint64Schema.refine(value => value !== '0', 'Expanded install reserve must be positive'),
  artifacts: z.array(recoveryArtifactSchema).max(FLEET_RECOVERY_MAX_ITEMS),
  targets: z.array(ownedTargetSchema).max(FLEET_RECOVERY_MAX_ITEMS),
  ownershipRules: z.array(ownershipRuleSchema).max(FLEET_RECOVERY_MAX_ITEMS),
  readiness: z.array(readinessProbeSchema).max(FLEET_RECOVERY_MAX_ITEMS),
  preserveRoots: z.array(fleetRecoveryPathSchema).max(FLEET_RECOVERY_MAX_ITEMS),
  minRescueProtocol: fleetRecoveryVersionSchema,
  authorityTransition: recoveryAuthorityTransitionSchema.optional(),
}).strict().superRefine((manifest, context) => {
  const issue = (path: (string | number)[], message: string) => context.addIssue({ code: 'custom', path, message });
  if (manifest.productVersion === FleetRecoveryRebootManifest.version) {
    if (!FleetRecoveryRebootManifest.matches(manifest as InstallationManifest)) issue([], 'Reboot authority cannot contain mutations');
    return;
  }
  for (const field of ['artifacts', 'targets', 'ownershipRules', 'readiness'] as const) {
    if (!manifest[field].length) issue([field], 'Product manifests require nonempty installation authority');
  }
  if (manifest.authorityTransition && manifest.minRescueProtocol < 3) issue(['minRescueProtocol'], 'Authority transitions require protocol three');
  const targetIds = new Set(manifest.targets.map(target => target.id));
  const rules = new Map(manifest.ownershipRules.map(rule => [rule.id, rule]));
  if (targetIds.size !== manifest.targets.length) issue(['targets'], 'Duplicate target IDs');
  if (rules.size !== manifest.ownershipRules.length) issue(['ownershipRules'], 'Duplicate ownership rule IDs');
  const artifactKeys = new Set(manifest.artifacts.map(artifact => `${artifact.role}:${artifact.sha256}`));
  if (artifactKeys.size !== manifest.artifacts.length) issue(['artifacts'], 'Duplicate artifact identities');
  const probeKeys = new Set<string>();
  manifest.readiness.forEach((probe, index) => {
    if (!targetIds.has(probe.targetId)) issue(['readiness', index, 'targetId'], 'Unresolved readiness target');
    const key = `${probe.targetId}:${probe.kind}`;
    if (probeKeys.has(key)) issue(['readiness', index], 'Duplicate readiness probe');
    probeKeys.add(key);
  });
  manifest.targets.forEach((target, index) => {
    const rule = rules.get(target.ownershipRuleId);
    if (!rule) { issue(['targets', index, 'ownershipRuleId'], 'Unresolved ownership rule'); return; }
    if (target.required && !manifest.readiness.some(probe => probe.targetId === target.id && probe.required)) {
      issue(['targets', index, 'required'], 'Required target needs mandatory readiness evidence');
    }
    if (target.kind === 'file' || target.kind === 'registry') {
      if (!rule.canonicalRoots.some(root => FleetRecoveryPathPolicy.isWithin(target.locator, root))) {
        issue(['targets', index, 'locator'], 'Target escapes reviewed ownership roots');
      }
    }
    if (target.scope === 'product' && target.desired === 'absent'
      && manifest.preserveRoots.some(root => FleetRecoveryPathPolicy.isWithin(target.locator, root)
        || FleetRecoveryPathPolicy.isWithin(root, target.locator))) {
      issue(['targets', index, 'locator'], 'Cleanup overlaps preserved root');
    }
    const configuration = target.configuration;
    if (configuration?.kind === 'device') {
      const driver = manifest.targets.find(candidate => candidate.id === configuration.expectedDriverTargetId);
      if (!driver || driver.kind !== 'driver' || driver.desired !== 'present'
        || driver.configuration?.kind !== 'driver' || !driver.configuration.hardwareIds.includes(configuration.hardwareId)) {
        issue(['targets', index, 'configuration'], 'Device must reference exact matching present driver');
      }
    }
    if (configuration?.kind === 'driver' && (!rule.infSha256.includes(configuration.infSha256)
      || !rule.catalogSha256.includes(configuration.catalogSha256))) {
      issue(['targets', index, 'configuration'], 'Driver package identities not authorized by ownership rule');
    }
    if (configuration?.kind === 'registry' && rule.registryView !== 'both' && rule.registryView !== configuration.view) {
      issue(['targets', index, 'configuration'], 'Registry view differs from ownership rule');
    }
    if (configuration && (configuration.kind === 'service' || configuration.kind === 'task')
      && !rule.canonicalRoots.some(root => FleetRecoveryPathPolicy.isWithin(configuration.executablePath, root))) {
      issue(['targets', index, 'configuration'], 'Executable escapes reviewed ownership roots');
    }
    if (target.expectedSha256 && !rule.knownSha256.includes(target.expectedSha256)) {
      issue(['targets', index, 'expectedSha256'], 'Expected hash not in reviewed ownership rule');
    }
    if (target.kind === 'registry' && rule.shared && target.desired === 'absent') {
      issue(['targets', index], 'Shared registry cleanup requires explicit value/element configuration');
    }
  });
});
